import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/site";
import logo from "@/assets/logo.png";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* keep trying */
      }
      tries += 1;
      if (tries >= MAX_POLLS) {
        setState("pending");
      } else {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 className="h-14 w-14 text-[#D4AF37] mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-3xl text-[#F9F6F0] mb-3">Confirming your payment…</h1>
          <p className="text-[#A19CA8]">Just a moment while we verify with Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-display text-4xl gold-text font-bold mb-3">Payment Confirmed!</h1>
          <p className="text-[#A19CA8] mb-8 max-w-md mx-auto">
            Thank you! Your payment is complete and your request has been received. A confirmation has been sent to our team, and we'll be in touch with next steps.
          </p>
          <HomeBtn />
        </>
      )}
      {state === "pending" && (
        <>
          <Loader2 className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-display text-3xl text-[#F9F6F0] mb-3">Payment Processing</h1>
          <p className="text-[#A19CA8] mb-8 max-w-md mx-auto">
            Your registration is saved. If you completed payment, it may take a moment to confirm. We'll follow up if anything is needed.
          </p>
          <HomeBtn />
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="h-14 w-14 text-[#A19CA8] mx-auto mb-6" />
          <h1 className="font-display text-3xl text-[#F9F6F0] mb-3">Something went wrong</h1>
          <p className="text-[#A19CA8] mb-8">We couldn't find your payment session.</p>
          <HomeBtn />
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-16 w-16 text-[#A19CA8] mx-auto mb-6" />
      <h1 className="font-display text-4xl text-[#F9F6F0] font-bold mb-3">Payment Cancelled</h1>
      <p className="text-[#A19CA8] mb-8 max-w-md mx-auto">
        No worries — your payment was not completed. You can register again anytime before August 14, 2026, or pay by CashApp / Apple Pay.
      </p>
      <HomeBtn />
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#06040A] flex items-center justify-center px-6" data-testid="payment-result">
    <div className="w-full max-w-lg text-center bg-[#130A1F]/70 border border-[#D4AF37]/25 p-10">
      <img src={logo} alt="The Change Center Church" className="h-16 w-16 object-cover rounded-lg border border-[#D4AF37]/40 mx-auto mb-8" />
      {children}
    </div>
  </div>
);

const HomeBtn = () => (
  <Link to="/" data-testid="payment-home-btn" className="inline-flex bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-8 py-3 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all">
    Back to Home
  </Link>
);
